import React, { useCallback, useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Tooltip from '@mui/material/Tooltip';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Divider from '@mui/material/Divider';
import EmailRoundedIcon from '@mui/icons-material/EmailRounded';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import LinkRoundedIcon from '@mui/icons-material/LinkRounded';
import SecurityRoundedIcon from '@mui/icons-material/SecurityRounded';
import { alpha } from '@mui/material/styles';
import { getPaymentLinkDeliveryResult, sendBookingPaymentLink } from '../../services/api';

export const PAYMENT_LINK_PREFLIGHT_MSG =
  'The Razorpay link is sent after the booking exists. Tap Confirm booking below — payment link options will appear on the confirmation screen.';

/** Flat or nested guest for enabling Email / WhatsApp actions. */
export function resolveGuestForPaymentLink(booking) {
  if (!booking) return null;
  if (booking.guest && typeof booking.guest === 'object') return booking.guest;
  const email = (booking.guest_email || booking.email || '').trim();
  const phone = (booking.guest_phone || booking.phone || '').trim();
  if (!email && !phone) return null;
  return { email, phone, full_name: booking.guest_name || '' };
}

function guestHasEmail(g) {
  const e = (g?.email || '').trim();
  return Boolean(e && e.includes('@'));
}

function guestHasPhone(g) {
  const digits = String(g?.phone || '').replace(/\D/g, '');
  return digits.length >= 6;
}

/**
 * Razorpay payment link send UI (email / WhatsApp / both).
 * Use inside a dialog (embedded) or wrapped by PaymentLinkDrawerOverlay.
 */
export default function PaymentLinkSendPanel({
  bookingId,
  booking,
  amount,
  currency = 'INR',
  isDark,
  accent = '#4ce1b6',
  onAfterSend,
  onSentSuccess,
  allowSend = true,
  sendBlockedMessage = '',
  canSendEmail = true,
  canSendWhatsApp = true,
  /** embedded = inside success dialog; overlay = full panel copy */
  layout = 'embedded',
}) {
  const [linkType, setLinkType] = useState('standard');
  const [busy, setBusy] = useState(null);
  const [inlineError, setInlineError] = useState('');

  const cur = (currency || 'INR').toUpperCase();
  const canUpi = cur === 'INR';
  const lt = !canUpi && linkType === 'upi' ? 'standard' : linkType;
  const guest = resolveGuestForPaymentLink(booking);
  const hasEmail = guestHasEmail(guest);
  const hasPhone = guestHasPhone(guest);
  const sendsEnabled = allowSend !== false && Number(bookingId) > 0;
  const emailEnabled = sendsEnabled && canSendEmail !== false;
  const whatsappEnabled = sendsEnabled && canSendWhatsApp !== false;
  const embedded = layout === 'embedded';

  useEffect(() => {
    setInlineError('');
  }, [bookingId, allowSend]);

  const formatMoney = useCallback(
    (n) => {
      const x = Number(n) || 0;
      try {
        return new Intl.NumberFormat(undefined, { style: 'currency', currency: cur }).format(x);
      } catch {
        return `${cur} ${x.toFixed(2)}`;
      }
    },
    [cur]
  );

  const send = useCallback(
    async (channel) => {
      if (!sendsEnabled) return;
      const id = Number(bookingId);
      if (!Number.isFinite(id) || id <= 0) {
        setInlineError('Missing booking.');
        return;
      }
      const v = Math.round(Number(amount) * 100) / 100;
      if (!Number.isFinite(v) || v <= 0) {
        setInlineError('Enter a valid payment amount in the form.');
        return;
      }
      setBusy(channel);
      setInlineError('');
      try {
        const res = await sendBookingPaymentLink(id, {
          channel,
          link_type: lt,
          amount: v,
        });
        const delivery = getPaymentLinkDeliveryResult(res, channel);
        if (!delivery.ok) {
          setInlineError(delivery.message);
        } else {
          onSentSuccess?.(delivery.message || 'Payment link sent.');
          await onAfterSend?.();
        }
      } catch (e) {
        setInlineError(e?.response?.data?.error || e?.message || 'Failed to send link.');
      } finally {
        setBusy(null);
      }
    },
    [amount, bookingId, lt, onAfterSend, onSentSuccess, sendsEnabled]
  );

  const sectionTitle = embedded ? 'Send payment link' : 'Send payment link';
  const muted = isDark ? '#94a3b8' : '#64748b';
  const heading = isDark ? '#f1f5f9' : '#0f172a';
  const panelBg = isDark ? alpha('#0f172a', 0.45) : alpha(accent, 0.06);
  const panelBorder = isDark ? alpha(accent, 0.28) : alpha(accent, 0.22);

  return (
    <Box
      sx={{
        width: '100%',
        ...(embedded
          ? {
              mt: 0.5,
              p: 1.75,
              borderRadius: 2,
              border: `1px solid ${panelBorder}`,
              bgcolor: panelBg,
            }
          : {}),
      }}
    >
      {embedded ? (
        <Divider sx={{ mb: 1.5, borderColor: isDark ? alpha('#fff', 0.08) : alpha('#000', 0.08) }} />
      ) : null}

      {!embedded ? (
        <>
          <Typography variant="subtitle1" sx={{ fontWeight: 800, color: heading }}>
            {sectionTitle}
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5, color: muted, lineHeight: 1.5 }}>
            Guests pay on Razorpay (PCI-DSS). Link amount matches the payment amount in this form.
          </Typography>
          <Typography
            variant="h6"
            sx={{ mt: 1, fontWeight: 800, color: isDark ? '#99f6e4' : '#0f766e' }}
          >
            {formatMoney(amount)}
          </Typography>
        </>
      ) : null}

      {!sendsEnabled && (sendBlockedMessage || '').trim() ? (
        <Alert severity="info" sx={{ mt: embedded ? 0 : 1.5, py: 0.25 }}>
          {sendBlockedMessage}
        </Alert>
      ) : null}

      {!guest && sendsEnabled && (
        <Alert severity="warning" sx={{ mt: embedded ? 0 : 1.5, py: 0.25 }}>
          Add a guest with email or phone before sending a link.
        </Alert>
      )}

      <Stack
        direction="row"
        spacing={0.75}
        alignItems="center"
        sx={{ mt: embedded ? 0 : 1.5, flexWrap: 'wrap', gap: 0.75 }}
      >
        <SecurityRoundedIcon sx={{ fontSize: 17, color: muted }} />
        <Typography variant="caption" sx={{ fontWeight: 700, color: isDark ? '#cbd5e1' : '#475569' }}>
          {embedded ? 'Send payment link' : 'Link type'}
        </Typography>
        <ToggleButtonGroup
          exclusive
          size="small"
          disabled={!sendsEnabled}
          value={canUpi ? linkType : 'standard'}
          onChange={(_, v) => v && setLinkType(v)}
          sx={{
            '& .MuiToggleButton-root': {
              px: 1.1,
              py: 0.35,
              textTransform: 'none',
              fontWeight: 700,
              fontSize: '0.72rem',
              borderColor: isDark ? alpha('#5eead4', 0.35) : alpha('#0f766e', 0.25),
              color: isDark ? '#e2e8f0' : '#334155',
            },
            '& .Mui-selected': {
              bgcolor: `${alpha(accent, isDark ? 0.28 : 0.18)} !important`,
              color: isDark ? '#ecfdf5' : '#0f172a',
            },
          }}
        >
          <ToggleButton value="standard">All methods</ToggleButton>
          <ToggleButton value="upi" disabled={!canUpi}>
            UPI-first
          </ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      {inlineError ? (
        <Alert severity="error" sx={{ mt: 1.5, py: 0.25 }} onClose={() => setInlineError('')}>
          {inlineError}
        </Alert>
      ) : null}

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 2 }}>
        {emailEnabled ? (
        <Tooltip title={!hasEmail ? 'Guest needs a valid email' : 'Email Razorpay checkout link'}>
          <span style={{ flex: 1, display: 'flex' }}>
            <Button
              fullWidth
              variant="contained"
              disabled={!guest || !hasEmail || Boolean(busy)}
              onClick={() => send('email')}
              startIcon={busy === 'email' ? <CircularProgress size={18} color="inherit" /> : <EmailRoundedIcon />}
              sx={{
                py: 0.95,
                textTransform: 'none',
                fontWeight: 800,
                fontSize: '0.8rem',
                background: `linear-gradient(135deg, #0d9488 0%, ${accent} 100%)`,
                color: '#000',
                '&:hover': { background: `linear-gradient(135deg, #0f766e 0%, #0d9488 100%)` },
              }}
            >
              Email link
            </Button>
          </span>
        </Tooltip>
        ) : null}
        {whatsappEnabled ? (
        <Tooltip title={!hasPhone ? 'Guest needs a phone for WhatsApp' : 'WhatsApp with secure link'}>
          <span style={{ flex: 1, display: 'flex' }}>
            <Button
              fullWidth
              variant="outlined"
              disabled={!guest || !hasPhone || Boolean(busy)}
              onClick={() => send('whatsapp')}
              startIcon={busy === 'whatsapp' ? <CircularProgress size={18} /> : <WhatsAppIcon />}
              sx={{
                py: 0.95,
                textTransform: 'none',
                fontWeight: 800,
                fontSize: '0.8rem',
                borderWidth: 2,
                borderColor: alpha('#25D366', 0.75),
                color: isDark ? '#bbf7d0' : '#047857',
                bgcolor: isDark ? alpha('#14532d', 0.35) : alpha('#dcfce7', 0.6),
                '&:hover': { borderWidth: 2, borderColor: '#22c55e' },
              }}
            >
              WhatsApp link
            </Button>
          </span>
        </Tooltip>
        ) : null}
        {emailEnabled && whatsappEnabled ? (
        <Tooltip title={!hasEmail || !hasPhone ? 'Guest needs both email and phone' : 'Send both channels'}>
          <span style={{ flex: 1, display: 'flex' }}>
            <Button
              fullWidth
              variant="outlined"
              disabled={!guest || !hasEmail || !hasPhone || Boolean(busy)}
              onClick={() => send('both')}
              startIcon={busy === 'both' ? <CircularProgress size={18} /> : <LinkRoundedIcon />}
              sx={{
                py: 0.95,
                textTransform: 'none',
                fontWeight: 800,
                fontSize: '0.8rem',
                borderColor: alpha(accent, 0.55),
                color: accent,
              }}
            >
              Both
            </Button>
          </span>
        </Tooltip>
        ) : null}
      </Stack>
    </Box>
  );
}
